<template>
  <div ref="host" class="frac-tank" :class="tankStyle" :style="{ width: widthPx + 'px' }">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RUNGS, SPILL_INCHES, type TankStyle } from './fracTank'

const props = withDefaults(defineProps<{
  name?: string
  capacityBbl?: number
  spillInches?: number
  oilInches?: number
  waterInches?: number
  fillToken?: string | number
  showRungLabels?: boolean
  live?: boolean
  widthPx?: number
  tankStyle?: TankStyle
}>(), {
  name: 'Tank',
  capacityBbl: 500,
  spillInches: SPILL_INCHES,
  oilInches: 0,
  waterInches: 0,
  showRungLabels: false,
  live: false,
  widthPx: 236,
  tankStyle: 'frac',
})

const host = ref<HTMLElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
let raf = 0
let visible = true
let reduced = false
let start = performance.now()
let fillStart = performance.now()
let replay = true
let churnUntil = 0
let lastToken = props.fillToken
let shownWater = 0
let shownOil = 0

const easeFill = (t: number, x1: number, y1: number, x2: number, y2: number) => {
  // cubic-bezier approximation
  const u = Math.min(1, Math.max(0, t))
  const cx = 3 * x1
  const bx = 3 * (x2 - x1) - cx
  const ax = 1 - cx - bx
  const cy = 3 * y1
  const by = 3 * (y2 - y1) - cy
  const ay = 1 - cy - by
  const sampleX = (s: number) => ((ax * s + bx) * s + cx) * s
  const sampleY = (s: number) => ((ay * s + by) * s + cy) * s
  let lo = 0
  let hi = 1
  let s = u
  for (let i = 0; i < 8; i++) {
    const x = sampleX(s) - u
    if (Math.abs(x) < 1e-4) break
    if (x > 0) hi = s
    else lo = s
    s = (lo + hi) / 2
  }
  return sampleY(s)
}

function targetCuts() {
  const spill = props.spillInches || SPILL_INCHES
  const water = Math.min(1, Math.max(0, props.waterInches / spill))
  const oil = Math.min(1 - water, Math.max(0, props.oilInches / spill))
  return { water, oil }
}

function tick(now: number) {
  const el = canvas.value
  if (!el) return
  const ctx = el.getContext('2d')
  if (!ctx) return

  const dpr = window.devicePixelRatio || 1
  const cssW = el.clientWidth || props.widthPx
  const cssH = el.clientHeight || Math.round(props.widthPx / 0.82)
  if (el.width !== Math.round(cssW * dpr) || el.height !== Math.round(cssH * dpr)) {
    el.width = Math.round(cssW * dpr)
    el.height = Math.round(cssH * dpr)
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cssW, cssH)

  const { water: tw, oil: to } = targetCuts()
  if (reduced) {
    shownWater = tw
    shownOil = to
  } else if (props.live) {
    shownWater += (tw - shownWater) * 0.22
    shownOil += (to - shownOil) * 0.22
  } else if (replay) {
    const elapsed = now - fillStart
    const wp = easeFill(elapsed / 1100, 0.16, 0.84, 0.22, 1)
    const op = easeFill(elapsed / 1350, 0.18, 0.7, 0.22, 1)
    shownWater = tw * wp
    shownOil = to * op
    if (elapsed >= 1350) replay = false
  } else {
    shownWater = tw
    shownOil = to
  }

  const churn = reduced || now > churnUntil ? 0 : (() => {
    const t = 1 - (churnUntil - now) / 1600
    return Math.max(0, 1 - t) ** 2
  })()
  const phase = reduced ? 0 : ((now - start) / 2800) * Math.PI * 2
  if (props.tankStyle === 'upright') drawUpright(ctx, cssW, cssH, shownWater, shownOil, phase, churn)
  else drawTank(ctx, cssW, cssH, shownWater, shownOil, phase, churn)

  raf = visible ? requestAnimationFrame(tick) : 0
}

function bodyPath(w: number, h: number) {
  const p = new Path2D()
  p.moveTo(w * 0.07, h * 0.08)
  p.lineTo(w * 0.91, h * 0.08)
  p.lineTo(w * 0.91, h * 0.79)
  p.quadraticCurveTo(w * 0.90, h * 0.88, w * 0.82, h * 0.91)
  p.lineTo(w * 0.16, h * 0.91)
  p.quadraticCurveTo(w * 0.08, h * 0.89, w * 0.07, h * 0.80)
  p.closePath()
  return p
}

function clipRect(w: number, h: number) {
  return {
    x: w * 0.13,
    y: h * 0.08,
    w: w * 0.77,
    h: h * 0.78,
  }
}

function waveY(x: number, width: number, surfaceY: number, freq: number, amp: number, phase: number) {
  return surfaceY + Math.sin((x / width) * freq * Math.PI * 2 + phase) * amp
}

function drawFluids(
  ctx: CanvasRenderingContext2D,
  clip: { x: number; y: number; w: number; h: number },
  waterR: number,
  oilR: number,
  phase: number,
  churn: number,
) {
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, clip.x, clip.y, clip.w, clip.h, Math.min(18, clip.w * 0.08))
  ctx.clip()
  ctx.fillStyle = '#1B252D'
  ctx.fillRect(clip.x, clip.y, clip.w, clip.h)

  const scale = clip.h / 200
  const waterAmp = Math.min(2.2, 0.9 + churn * 1.6) * scale
  const oilAmp = waterAmp * 0.72
  const waterTop = clip.y + clip.h * (1 - waterR)
  const oilTop = clip.y + clip.h * (1 - waterR - oilR)

  if (waterR > 0.001) {
    const g = ctx.createLinearGradient(0, waterTop, 0, clip.y + clip.h)
    g.addColorStop(0, '#7EE9FF')
    g.addColorStop(0.22, '#2EB6E6')
    g.addColorStop(0.62, '#1784B8')
    g.addColorStop(1, '#0B4F78')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(clip.x, clip.y + clip.h)
    for (let x = clip.x; x <= clip.x + clip.w; x += 2) {
      ctx.lineTo(x, waveY(x - clip.x, clip.w, waterTop, 2.05, waterAmp, phase))
    }
    ctx.lineTo(clip.x + clip.w, clip.y + clip.h)
    ctx.closePath()
    ctx.fill()
  }

  if (oilR > 0.001) {
    const g = ctx.createLinearGradient(0, oilTop, 0, waterTop)
    g.addColorStop(0, '#E8B45AEE')
    g.addColorStop(0.38, '#C48432DD')
    g.addColorStop(1, '#8A5A16')
    ctx.fillStyle = g
    ctx.beginPath()
    const oilPhase = phase * 0.62 + 0.7
    ctx.moveTo(clip.x, waveY(0, clip.w, oilTop, 1.35, oilAmp, oilPhase))
    for (let x = clip.x; x <= clip.x + clip.w; x += 2) {
      ctx.lineTo(x, waveY(x - clip.x, clip.w, oilTop, 1.35, oilAmp, oilPhase))
    }
    for (let x = clip.x + clip.w; x >= clip.x; x -= 2) {
      ctx.lineTo(x, waveY(x - clip.x, clip.w, waterTop, 2.05, waterAmp, phase))
    }
    ctx.closePath()
    ctx.fill()
  }

  if (waterR > 0.001 && oilR > 0.001) {
    ctx.strokeStyle = 'rgba(180, 236, 255, 0.85)'
    ctx.lineWidth = 1.4
    ctx.beginPath()
    for (let x = clip.x; x <= clip.x + clip.w; x += 2) {
      const y = waveY(x - clip.x, clip.w, waterTop, 2.05, waterAmp, phase)
      if (x === clip.x) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.stroke()
  }

  ctx.strokeStyle = '#E24A4A'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(clip.x + 6, clip.y + 4)
  ctx.lineTo(clip.x + clip.w - 6, clip.y + 4)
  ctx.stroke()
  ctx.restore()
}

function drawTank(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  waterR: number,
  oilR: number,
  phase: number,
  churn: number,
) {
  const body = bodyPath(w, h)
  const steel = ctx.createLinearGradient(0, 0, w, h)
  steel.addColorStop(0, '#333E46')
  steel.addColorStop(0.5, '#242D34')
  steel.addColorStop(1, '#1B2329')
  ctx.fillStyle = steel
  ctx.fill(body)
  ctx.strokeStyle = '#59666F'
  ctx.lineWidth = w * 0.012
  ctx.stroke(body)

  ctx.fillStyle = '#333E46'
  roundRect(ctx, w * 0.14, h * 0.025, w * 0.70, h * 0.07, w * 0.045)
  ctx.fill()
  ctx.strokeStyle = '#59666F'
  ctx.lineWidth = w * 0.008
  ctx.stroke()

  const clip = clipRect(w, h)
  drawFluids(ctx, clip, waterR, oilR, phase, churn)

  ctx.save()
  ctx.beginPath()
  roundRect(ctx, clip.x, clip.y, clip.w, clip.h, 18)
  ctx.clip()
  ctx.strokeStyle = 'rgba(154,168,178,0.8)'
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.moveTo(clip.x + 10, clip.y + 6)
  ctx.lineTo(clip.x + 10, clip.y + clip.h - 8)
  ctx.moveTo(clip.x + clip.w - 10, clip.y + 6)
  ctx.lineTo(clip.x + clip.w - 10, clip.y + clip.h - 8)
  ctx.stroke()
  RUNGS.forEach((inches) => {
    const y = clip.y + clip.h * (1 - inches / (props.spillInches || SPILL_INCHES))
    ctx.beginPath()
    ctx.moveTo(clip.x + 8, y)
    ctx.lineTo(clip.x + clip.w - 8, y)
    ctx.stroke()
    if (props.showRungLabels) {
      ctx.fillStyle = '#EAF2F8'
      ctx.font = `700 ${Math.max(9, w * 0.042)}px Inter, system-ui, sans-serif`
      ctx.textAlign = 'right'
      ctx.fillText(`${Math.round(inches / 12)}'`, clip.x + clip.w - 14, y - 3)
    }
  })
  ctx.restore()

  ctx.strokeStyle = '#151B20'
  ctx.lineWidth = w * 0.012
  for (let i = 1; i <= 6; i++) {
    const x = w * (0.13 + i * 0.105)
    ctx.beginPath()
    ctx.moveTo(x, h * 0.10)
    ctx.lineTo(x, h * 0.78)
    ctx.stroke()
  }
  ctx.beginPath()
  ctx.moveTo(w * 0.08, h * 0.22)
  ctx.lineTo(w * 0.90, h * 0.22)
  ctx.moveTo(w * 0.08, h * 0.55)
  ctx.lineTo(w * 0.90, h * 0.55)
  ctx.stroke()

  ctx.strokeStyle = '#D2B45C'
  ctx.lineCap = 'round'
  ctx.lineWidth = w * 0.017
  ctx.beginPath()
  ctx.moveTo(w * 0.13, h * 0.12)
  ctx.lineTo(w * 0.39, h * 0.12)
  ctx.moveTo(w * 0.13, h * 0.12)
  ctx.lineTo(w * 0.13, h * 0.37)
  ctx.moveTo(w * 0.20, h * 0.16)
  ctx.lineTo(w * 0.55, h * 0.68)
  ctx.moveTo(w * 0.26, h * 0.16)
  ctx.lineTo(w * 0.61, h * 0.68)
  ctx.stroke()
  ctx.lineWidth = w * 0.009
  ctx.beginPath()
  for (let i = 0; i < 8; i++) {
    const t = i / 7
    const x = w * (0.23 + 0.35 * t)
    const y = h * (0.20 + 0.44 * t)
    ctx.moveTo(x - w * 0.025, y)
    ctx.lineTo(x + w * 0.030, y)
  }
  for (let i = 0; i < 4; i++) {
    const x = w * (0.15 + i * 0.075)
    ctx.moveTo(x, h * 0.12)
    ctx.lineTo(x, h * 0.26)
  }
  ctx.moveTo(w * 0.13, h * 0.26)
  ctx.lineTo(w * 0.39, h * 0.26)
  ctx.stroke()

  const eqY = h * 0.71
  roundRect(ctx, w * 0.16, eqY, w * 0.68, h * 0.045, 6)
  ctx.fillStyle = '#4B555D'
  ctx.fill()
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 2
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(w * 0.28, h * 0.78, h * 0.055, 0, Math.PI * 2)
  ctx.fillStyle = '#333E46'
  ctx.fill()
  ctx.strokeStyle = '#67727A'
  ctx.lineWidth = 4
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(w * 0.68, h * 0.82, h * 0.028, 0, Math.PI * 2)
  ctx.fillStyle = '#525C62'
  ctx.fill()
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.strokeStyle = '#9BA7AE'
  ctx.lineWidth = 1.4
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2
    ctx.beginPath()
    ctx.moveTo(w * 0.68 + Math.cos(a) * h * 0.01, h * 0.82 + Math.sin(a) * h * 0.01)
    ctx.lineTo(w * 0.68 + Math.cos(a) * h * 0.022, h * 0.82 + Math.sin(a) * h * 0.022)
    ctx.stroke()
  }

  ctx.fillStyle = '#EAF2F8'
  ctx.textAlign = 'right'
  ctx.font = `900 ${Math.max(14, w * 0.078)}px Inter, system-ui, sans-serif`
  ctx.fillText(props.name, w * 0.88, h * 0.16)
  ctx.fillStyle = '#92A5B7'
  ctx.font = `600 ${Math.max(10, w * 0.046)}px Inter, system-ui, sans-serif`
  const total = (waterR + oilR) * props.capacityBbl
  ctx.fillText(`${total.toFixed(1)} / ${props.capacityBbl.toFixed(0)} BBL`, w * 0.88, h * 0.205)
}

function drawUpright(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  waterR: number,
  oilR: number,
  phase: number,
  churn: number,
) {
  const left = w * 0.045
  const bodyW = w * 0.91
  const cx = left + bodyW / 2
  const top = h * 0.048
  const bodyH = h * 0.88
  const bottom = top + bodyH
  const fill = Math.min(1, Math.max(0, waterR + oilR))
  const rim = Math.max(3, w * 0.018)
  const stickW = Math.max(12, w * 0.055)
  const clip = {
    x: left + rim,
    y: top + 6,
    w: bodyW - rim * 2,
    h: bodyH - 16,
  }

  ctx.fillStyle = '#F3EDE3'
  ctx.textAlign = 'center'
  ctx.font = `800 ${Math.max(11, w * 0.062)}px Inter, system-ui, sans-serif`
  ctx.fillText(props.name, cx, h * 0.032)
  ctx.fillStyle = '#9AA6B2'
  ctx.font = `600 ${Math.max(9, w * 0.04)}px Inter, system-ui, sans-serif`
  ctx.fillText(`${((waterR + oilR) * props.capacityBbl).toFixed(1)} / ${props.capacityBbl.toFixed(0)} BBL`, cx, h * 0.988)

  ctx.fillStyle = '#1A1A1A'
  ctx.beginPath()
  ctx.ellipse(cx, bottom - 2, bodyW / 2 + 1, 5, 0, 0, Math.PI * 2)
  ctx.fill()

  const shell = ctx.createLinearGradient(left, 0, left + bodyW, 0)
  shell.addColorStop(0, '#D8D2C6')
  shell.addColorStop(0.18, '#F7F3EC')
  shell.addColorStop(0.38, '#FFFDF8')
  shell.addColorStop(0.62, '#EFE9DE')
  shell.addColorStop(0.86, '#D9D3C7')
  shell.addColorStop(1, '#C8C2B6')
  ctx.fillStyle = shell
  ctx.beginPath()
  ctx.moveTo(left, top + 8)
  ctx.quadraticCurveTo(left, top, left + 10, top)
  ctx.lineTo(left + bodyW - 10, top)
  ctx.quadraticCurveTo(left + bodyW, top, left + bodyW, top + 8)
  ctx.lineTo(left + bodyW, bottom - 6)
  ctx.lineTo(left, bottom - 6)
  ctx.closePath()
  ctx.fill()

  drawFluids(ctx, clip, waterR, oilR, phase, churn)

  const railX = clip.x + 5
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, clip.x, clip.y, clip.w, clip.h, Math.min(14, clip.w * 0.06))
  ctx.clip()
  ctx.strokeStyle = 'rgba(248, 244, 236, 0.55)'
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(railX, clip.y + 4)
  ctx.lineTo(railX, clip.y + clip.h - 6)
  ctx.moveTo(railX + 10, clip.y + 4)
  ctx.lineTo(railX + 10, clip.y + clip.h - 6)
  ctx.stroke()
  ctx.lineWidth = 1.4
  ctx.beginPath()
  for (let i = 0; i < 20; i++) {
    const y = clip.y + 6 + ((clip.h - 12) * i) / 19
    ctx.moveTo(railX, y)
    ctx.lineTo(railX + 10, y)
  }
  ctx.stroke()
  ctx.restore()

  const manX = left + 8
  const manY = top + 16
  ctx.beginPath()
  ctx.arc(manX, manY, Math.max(4, w * 0.02), 0, Math.PI * 2)
  ctx.fillStyle = '#F8F4ED'
  ctx.fill()
  ctx.strokeStyle = '#B7B0A4'
  ctx.lineWidth = 2
  ctx.stroke()

  const scaleTop = clip.y + 2
  const scaleBot = clip.y + clip.h - 2
  const scaleH = scaleBot - scaleTop
  const stickX = clip.x + clip.w - stickW - 3
  ctx.fillStyle = 'rgba(255,255,255,0.78)'
  ctx.fillRect(stickX, scaleTop, stickW, scaleH)
  ctx.strokeStyle = 'rgba(180,176,168,0.7)'
  ctx.lineWidth = 1
  ctx.strokeRect(stickX, scaleTop, stickW, scaleH)

  ctx.fillStyle = '#111111'
  ctx.strokeStyle = '#111111'
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  ctx.font = `700 ${Math.max(7, w * 0.038)}px Inter, system-ui, sans-serif`
  for (let foot = 1; foot <= 20; foot++) {
    const y = scaleTop + ((foot - 1) / 19) * scaleH
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(stickX + 1, y)
    ctx.lineTo(stickX + stickW * 0.42, y)
    ctx.stroke()
    ctx.fillText(`${foot}'`, stickX + stickW - 1.5, y)
  }

  if (fill > 0.004) {
    const markY = scaleBot - fill * scaleH
    ctx.fillStyle = '#D21F2A'
    ctx.beginPath()
    ctx.moveTo(stickX + stickW + 1, markY)
    ctx.lineTo(stickX + stickW + 7, markY - 4)
    ctx.lineTo(stickX + stickW + 7, markY + 4)
    ctx.closePath()
    ctx.fill()
  }

  ctx.fillStyle = '#111111'
  ctx.fillRect(left - 1, bottom - 7, bodyW + 2, 7)
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function bumpFill() {
  replay = !props.live
  fillStart = performance.now()
  churnUntil = performance.now() + 1600
}

watch(() => props.fillToken, (token) => {
  if (token !== lastToken) {
    lastToken = token
    bumpFill()
  }
})

watch(() => [props.oilInches, props.waterInches], () => {
  if (props.live) churnUntil = performance.now() + 1600
})

let io: IntersectionObserver | null = null
onMounted(() => {
  reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  bumpFill()
  io = new IntersectionObserver((entries) => {
    visible = entries.some((e) => e.isIntersecting)
    if (visible && !raf) raf = requestAnimationFrame(tick)
  }, { threshold: 0.05 })
  if (host.value) io.observe(host.value)
  raf = requestAnimationFrame(tick)
})

onBeforeUnmount(() => {
  io?.disconnect()
  cancelAnimationFrame(raf)
})
</script>

<style scoped>
.frac-tank {
  aspect-ratio: 0.82;
  height: auto;
}
.frac-tank.upright {
  aspect-ratio: 0.62;
  width: 100%;
  max-width: 260px;
  margin: 0 auto;
}
canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
